import { useWidget } from "../../../providers/widget"
import { SpecsWidgetConfig } from "../config"
import Stylesheet from "../../../components/Stylesheet"
import { useMemo } from "react"
import SpecsList from "./SpecsList"
import WebFont from "../../../components/WebFont"
import WidgetSection from "../../../components/WidgetSection"
import { useFetchSpecs } from "../data"
import { resolveWidgetSpacingStyle } from "@homerunner-next/widget-core/spacing"
import { hostFontSizeCss } from "../../../common/host-scope"

/**
 * Dark-palette overrides for the auto FIRST-PAINT path (#900). The inline
 * Stylesheet below always emits the LIGHT palette (so light output stays
 * byte-identical); this appends the DARK palette under two selector prefixes:
 *   - `:host([data-hr-theme="dark"])` — the runtime marker (explicit dark +
 *     post-hydration auto), same values the `.homerunner-specs-container.dark`
 *     class + `resolvedTheme==='dark'` accent branch already produce.
 *   - inside `@media (prefers-color-scheme: dark)`,
 *     `:host([data-hr-scheme="auto"]:not([data-hr-theme]))` — a server-stamped
 *     auto widget on a dark OS, painting dark AT PARSE with no JS/cookie; the
 *     `:not` drops it the instant the runtime stamps the marker at hydration.
 * Computed from `darkModeColors` directly (not gated on `resolvedTheme`) so the
 * dark palette ships on every render — auto first paint can't wait for the
 * theme to resolve. `:host(...)` (not a descendant `[data-hr-theme]`) is
 * required to reach the shadow host from inside its own stylesheet, and BOTH
 * conditions of the auto selector must live INSIDE `:host(...)` or the `:not`
 * silently never matches the host.
 */
function specsDarkOverrides(
  prefix: string,
  dc: SpecsWidgetConfig['darkModeColors'],
): string {
  return `
          ${prefix} .homerunner-specs-container {
            color: var(--foreground) !important;
          }
          ${dc.accent ? `
          ${prefix} .text-accent {
            color: ${dc.accent} !important;
          }
          ${prefix} .bg-accent {
            background-color: ${dc.accent} !important;
          }
          ${prefix} .border-accent {
            border-color: ${dc.accent} !important;
          }
          ` : ''}`
}

export function SpecsWidgetContent() {
  const { options, resolvedTheme, widgetId } = useWidget<SpecsWidgetConfig>()

  const colors = useMemo(() => {
    return resolvedTheme === 'dark' ? options.darkModeColors : options.lightModeColors
  }, [resolvedTheme, options.lightModeColors, options.darkModeColors])

  // Section title only renders once we have actual property data.
  // React Query dedupes with the call inside <SpecsList />.
  const { data } = useFetchSpecs(options.filter)
  const hasContent = Boolean(data && (data.bedroom_count > 0 || data.bed_count > 0 || data.bathroom_count))

  return (
    <div
      className={`homerunner-specs-container homerunner-specs-app w-full ${resolvedTheme === 'dark' ? 'dark' : ''}`}
      style={resolveWidgetSpacingStyle(options)}
      suppressHydrationWarning
    >
      <WebFont icoFont={true} />
      <Stylesheet
        css={`
          .homerunner-specs-container *:not([class^=icon-]) {
            font-family: '${options.font.family}' !important;
          }
          .homerunner-specs-container.dark {
            color: var(--foreground) !important;
          }
          ${hostFontSizeCss(widgetId, options.font.size)}
          .text2x {
            font-size: ${options.font.size * 2}px !important;
          }
          ${colors.accent ? `
          .text-accent {
            color: ${colors.accent} !important;
          }
          .bg-accent {
            background-color: ${colors.accent} !important;
          }
          .border-accent {
            border-color: ${colors.accent} !important;
          }
          ` : ''}
          /* Dark palette for the auto first-paint path (#900) plus the runtime
             marker. The rules above always emit LIGHT; these override only under
             dark selectors, so light rendering is byte-identical to before. */
          ${specsDarkOverrides(':where(:host([data-hr-theme="dark"]))', options.darkModeColors)}
          @media (prefers-color-scheme: dark) {
            ${specsDarkOverrides(':where(:host([data-hr-scheme="auto"]:not([data-hr-theme])))', options.darkModeColors)}
          }
          ${options.customCss ?? ''}
        `}
      />
      {hasContent && (
        <WidgetSection show={options.section?.show} title={options.section?.title} />
      )}
      <SpecsList />
    </div>
  )
}
